/**
 * caseStatus — the Case Home status card's model. s124 (Dr. Rami, TestFlight build 91: "Reconsider where status is
 * displayed. Make more prominent at the top of screen so user knows what is happening at any given time"; "Mock up ways
 * to make the status more obvious maybe merge with the orella dialogue bit"; canvas "Orella Case Home — Revised" boards
 * A–C and copy doc ORELLA_CASE_HOME_COPY_v1 §2 APPROVED 2026-10-30). PURE: CaseHome hands it the facts it already
 * computes (sections, casePhase, status, …) and draws what comes back.
 *
 *   pill  — the i18n key (caseHome.*) of the one status line, and its tone (amber | blue | green)
 *   step  — 1..4, the step the case is on (activeStageIndex + 1); segs — one 'done' | 'now' | 'todo' per step
 *   next  — the i18n key of the "Next: …" headline, or null when Orella's own message already says it
 *   actionIndex — the section whose action the card's button runs (the live step, when it can be acted on), or -1
 */
import { activeStageIndex } from './stageModel.js'

export const PILL_TONES = {
  amber: { bg: '#FDF1DC', fg: '#8A5300', dot: '#D98A00' },
  blue: { bg: '#E6F3FE', fg: '#1B5E96', dot: '#2E7BC4' },
  green: { bg: '#DDF3E2', fg: '#1C6B33', dot: '#2F9E55' },
}

export function caseStatusModel({
  sections = [], casePhase = 1, status = '', codeShared = false, external = false, isFollowUp = false,
  diagnoseAcknowledged = false, deliverState = '', shareInline = false,
} = {}) {
  const idx = sections.length ? activeStageIndex(sections) : 0
  const step = Math.min(4, Math.max(1, idx + 1))
  const segs = sections.map((s, i) => (s.complete ? 'done' : i === idx ? 'now' : 'todo'))
  const needsReview = sections.some((s) => s.needsReview)
  const allDone = deliverState === 'all-done' || deliverState === 'complete'

  let pill = 'pillSentWaiting'; let tone = 'blue'; let next = 'nextWaitAccept'
  if (isFollowUp || casePhase >= 6) {
    if (needsReview || (!isFollowUp && !diagnoseAcknowledged)) { pill = 'pillResultsReady'; tone = 'green'; next = 'nextReview' }
    else if (allDone) { pill = 'pillComplete'; tone = 'green'; next = 'nextDone' }
    else { pill = 'pillCarePlan'; tone = 'blue'; next = 'nextDeliver' }
  } else if (casePhase <= 2) { pill = 'pillDraft'; tone = 'amber'; next = 'nextDescribe' }
  else if (casePhase === 3) { pill = 'pillReadyToShare'; tone = 'blue'; next = 'nextShare' }
  else if (status === 'checked_in') { pill = 'statusCheckedIn'; next = null }
  else if (status === 'scheduled') { pill = 'pillScheduled'; next = external ? 'nextVisit' : 'nextCheckIn' }
  else if (status === 'claimed' || status === 'in_progress') { pill = 'pillAccepted'; next = external ? 'nextVisit' : null }
  else if (codeShared) { pill = 'pillCodeActive'; next = 'nextVisit' }
  else if (external) { pill = 'statusVisitPrep'; next = 'nextVisit' }

  const live = sections[idx]
  const actionable = !!live && !live.complete && (live.available || live.needsReview || live.externalUpload)
  // Phase 3's primary is the inline Share button (check-share-button-prominent): the card adds no second one.
  const actionIndex = actionable && !(casePhase === 3 && shareInline) ? idx : -1
  return { pill, tone, step, segs, next, actionIndex }
}
